import { useEffect, useState, useRef } from 'react';
import {
  fetchUASFM,
  deriveAdvisory,
  AdvisoryResult,
  UASFMResult,
} from '../utils/airspace-helpers';
import Alert from '../components/ui/Alert';
import Reveal from '../components/ui/Reveal';
import FadeIn from '../components/ui/FadeIn';
import Appear from '../components/ui/Appear';
import Skeleton, { SkeletonGroup } from '../components/ui/Skeleton';

interface Props {
  lat: number | null | undefined;
  lng: number | null | undefined;
  eventDate?: string | null;
}

const LEVEL_TONE = { green: 'success', amber: 'warning', red: 'error' } as const;

/**
 * Producer-side airspace advisory shown in EventForm under the LocationPicker.
 *
 * Triggers on launch coordinate change with an 800ms debounce, calls UASFM
 * (direct to FAA — public, CORS-enabled, no auth) and shows a traffic-light
 * summary. NOTAMs are NOT fetched here — pilots verify TFRs / restrictions
 * via the FAA NOTAM Search link on the /pilots/airspace event detail page.
 */
const AirspaceAdvisoryPanel: React.FC<Props> = ({ lat, lng }) => {
  const [advisory, setAdvisory] = useState<AdvisoryResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) {
      setAdvisory(null);
      setError(null);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const reqId = ++reqIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const u: UASFMResult = await fetchUASFM(lat, lng);
        if (reqId !== reqIdRef.current) return;
        setAdvisory(deriveAdvisory(u, []));
      } catch (e: any) {
        if (reqId !== reqIdRef.current) return;
        setAdvisory(deriveAdvisory(null, []));
        setError(e?.message || 'Lookup failed');
      } finally {
        if (reqId === reqIdRef.current) setLoading(false);
      }
    }, 800);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [lat, lng]);

  const showSkeleton = lat != null && lng != null && loading && !advisory;
  const showAlert = lat != null && lng != null && !showSkeleton && !!advisory;

  // Reveal keeps the last content mounted through its exit, so clearing the coordinates fades the panel out.
  return (
    <Reveal show={showSkeleton || showAlert}>
      {showSkeleton || !advisory ? (
        <FadeIn key="loading">
          <SkeletonGroup label="Checking airspace" className="mt-3">
            <Skeleton className="h-14 w-full rounded-panel" />
          </SkeletonGroup>
        </FadeIn>
      ) : (
        <FadeIn key="advisory">
          <Alert
            tone={LEVEL_TONE[advisory.level]}
            live="status"
            className="mt-3"
            title={
              <>
                {advisory.headline}
                <Appear show={loading} className="ml-2 text-[10px] font-normal text-on-surface-variant italic">refreshing…</Appear>
              </>
            }
          >
            <p className="m-0 text-xs">{advisory.detail}</p>
            <Reveal show={!!error}>
              <p className="m-0 mt-1 text-[10px] text-amber-200/80">{error}</p>
            </Reveal>
            <p className="m-0 mt-2 text-[10px] text-on-surface-variant">
              Pilots will review this independently — informational only.
            </p>
          </Alert>
        </FadeIn>
      )}
    </Reveal>
  );
};

export default AirspaceAdvisoryPanel;
